import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Message, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessagesModule } from 'primeng/messages';
import { translatedGenderTypes } from 'src/app/demo/constants/translated_genders';
import { Gender } from 'src/app/demo/enums/gender';
import { Option } from 'src/app/demo/models/common-data-types';
import { CountryMetadata } from 'src/app/demo/models/country-metadata';
import { DateValidationObject } from 'src/app/demo/models/validate-date';
import { CountryService } from 'src/app/demo/service/country.service';
import { CustomToastrService } from 'src/app/demo/service/custom-toastr.service';
import { DriversService } from 'src/app/demo/service/drivers.service';
import { LanguageService } from 'src/app/demo/service/language.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { validateDate } from 'src/app/demo/utilities/date-validators';
import { mapToAddDriver } from 'src/app/demo/utilities/driver-utils';
import { ToastModule } from 'primeng/toast';

@Component({
    selector: 'app-add-driver',
    standalone: true,
    imports: [
        ButtonModule,
        CommonModule,
        TranslateModule,
        FormsModule,
        RouterModule,
        MessagesModule,
        ReactiveFormsModule,
        ToastModule,
        DropdownModule,
        CalendarModule,
        CardModule,
        InputTextModule,
    ],
    templateUrl: './add-driver.component.html',
    styleUrl: './add-driver.component.scss',
    providers: [MessagesModule, MessageService],
})
export class AddDriverComponent implements OnInit {
    driverForm!: FormGroup;
    genderTypes: any[] = [
        { name: 'Male', value: Gender.Male },
        { name: 'Female', value: Gender.Female },
    ];
    selectedGender: string = '';
    dateValidator: DateValidationObject = validateDate(16);
    messages: Message[] = [];
    idTypes!: Option[];
    countries!: CountryMetadata[];
    selectedLanguage: any = null;
    translatedGenderTypes: any = translatedGenderTypes;

    showIdMisMatchMessage: boolean = false;

    ngOnInit() {
        this.initializeDriverForm();
        this.fetchIdentificationTypes();
        this.fetchCountries();
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translate.use(language);
        this.languageService.setLanguage(language);
        this.translateDropdowns();
    }

    languageChanged(event: DropdownChangeEvent) {
        this.setLanguage(event.value);
    }

    initializeDriverForm() {
        this.driverForm = this.fb.group(
            {
                firstName: ['', [Validators.required]],
                surname: ['', [Validators.required]],
                dateOfBirth: ['', [Validators.required]],
                gender: [this.selectedGender],
                nationalIdentification: [
                    '',
                    [Validators.required, Validators.maxLength(13)],
                ],
                confirmNationalIdentification: [
                    '',
                    [Validators.required, Validators.maxLength(13)],
                ],
                identificationType: ['', [Validators.required]],
                countryOfIDIssue: ['', [Validators.required]],
                nationality: ['', [Validators.required]],
            },
            { validators: this.matchNationalIDs }
        );
    }

    constructor(
        private progressLoader: ProgressLoaderService,
        private languageService: LanguageService,
        private translate: TranslateService,
        private router: Router,
        private wiseToastr: CustomToastrService,
        private messageService: MessageService,
        private fb: FormBuilder,
        private driverService: DriversService,
        private countryService: CountryService
    ) {}

    onDriverSubmit() {
        this.progressLoader.show('Saving Driver ...');
        const driverPayload = mapToAddDriver(this.driverForm);
        this.driverService.addDriver(driverPayload).subscribe({
            next: (value) => {
                this.progressLoader.hide();
                this.wiseToastr.showSuccess(this.translate.instant('New driver')+this.translate.instant("was added successfully, You now need to upload the driver's documents"))
                setTimeout(() => {
                    this.router.navigate(['/home/drivers', value.driver.id]);
                }, 4000);
            },
            error: (err) => {
                this.progressLoader.hide();
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
                this.progressLoader.hide();
            },
        });
    }

    matchNationalIDs(group: FormGroup) {
        const nationalID = group.get('nationalIdentification')?.value;
        const confirmID = group.get('confirmNationalIdentification')?.value;
        return nationalID === confirmID ? null : { notMatching: true };
    }

    onConfirmIDKeyUp(): void {
        this.showIdMisMatchMessage =
            this.driverForm.get('nationalIdentification')?.value !== '' &&
            this.driverForm.get('confirmNationalIdentification')?.value !==
                this.driverForm.get('nationalIdentification')?.value;
    }

    fetchIdentificationTypes() {
        this.countryService.getIdentificationTypes().subscribe({
            next: (value) => {
                this.idTypes = value.map((e) => {
                    let translatedString = '';
                    this.translate.get(e.name).subscribe((result) => {
                        translatedString = result;
                    });

                    return {
                        name: translatedString,
                        value: e.name,
                    };
                });
            },
            error: (err) => {
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
            },
        });
    }

    fetchCountries() {
        this.countryService.getCountryMetadata().subscribe({
            next: (value) => {
                this.countries = value;
            },
            error: (err) => {
                this.wiseToastr.showError(this.translate.instant(err.error.messageKey))
            },
        });
    }

    translateDropdowns() {
        if (this.idTypes) {
            
        } else {
            return;
        }
    }

    onGenderChange(event: DropdownChangeEvent) {
        this.selectedGender = event.value;
    }

    
}
